import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { FormControl, MenuItem, OutlinedInput, Select } from '@mui/material';
import { ModalShell } from '../modalsShared';
import CommonDatePicker from '../../../../Components/Datepicker/datepicker';
import dropdwn from '../../../../Assets/images/svg/dar_dropdown_arrow.svg';
import {
  buildValidStatusMessage,
  useProjectStatusOptions,
} from './statusOptionsShared';

const DEFAULT_FORM_STATE = {
  newStatus: '',
  effectiveDate: '',
  remarks: '',
};

const getCurrentTimeString = () => {
  const now = new Date();
  return now.toTimeString().split(' ')[0];
};

const ProjectNameBanner = ({ projectName }) => {
  if (!projectName) return null;

  return (
    <div className="request-history-project-banner">
      <span className="request-history-project-banner__label">Project Name</span>
      <span className="request-history-project-banner__value">{projectName}</span>
    </div>
  );
};

const AddRequestModal = ({
  isOpen,
  onClose,
  onSubmit,
  projectName = '',
  currentStatus = '',
  isBlocked = false,
  isSubmitDisabled = false,
  blockedMessage = '',
  title = 'Add request',
  submitLabel = 'Add request',
  cancelLabel = 'Cancel',
  headerAction = null,
}) => {
  const [formData, setFormData] = useState(DEFAULT_FORM_STATE);
  const [formError, setFormError] = useState('');
  const {
    statusOptions,
    isLoading: isLoadingStatusOptions,
    error: statusOptionsError,
  } = useProjectStatusOptions({
    isOpen,
    includeValue: formData.newStatus,
  });
  const validStatusMessage = useMemo(
    () => buildValidStatusMessage(statusOptions),
    [statusOptions],
  );
  const isStatusSelectionDisabled = isBlocked
    || isSubmitDisabled
    || isLoadingStatusOptions
    || Boolean(statusOptionsError);
  const normalizedCurrentStatus = useMemo(
    () => String(currentStatus || '')
      .trim()
      .toLowerCase(),
    [currentStatus],
  );
  const isSameStatusSelected = useMemo(
    () => (
      normalizedCurrentStatus
      && String(formData.newStatus || '')
        .trim()
        .toLowerCase() === normalizedCurrentStatus
    ),
    [formData.newStatus, normalizedCurrentStatus],
  );
  const isSubmitBlockedByValidation = isSameStatusSelected;

  useEffect(() => {
    if (!isOpen) return;
    setFormError('');
    if (!isBlocked) return;
    setFormData(DEFAULT_FORM_STATE);
  }, [isBlocked, isOpen]);

  useEffect(() => {
    if (!isOpen || isBlocked) return;

    if (isSameStatusSelected) {
      setFormError('Current project status and new status cannot be the same.');
      return;
    }

    setFormError((currentError) => (
      currentError === 'Current project status and new status cannot be the same.'
        ? ''
        : currentError
    ));
  }, [isBlocked, isOpen, isSameStatusSelected]);

  const handleClose = () => {
    setFormData(DEFAULT_FORM_STATE);
    setFormError('');
    onClose();
  };

  const handleSubmit = async (event) => {
    if (event?.preventDefault) {
      event.preventDefault();
    }

    if (isBlocked || isSubmitDisabled || isLoadingStatusOptions) return;

    if (statusOptionsError || !statusOptions.length) {
      setFormError(statusOptionsError || 'No project statuses are available.');
      return;
    }

    const allowed = statusOptions.map((option) => option.toLowerCase());
    const normalizedStatus = String(formData.newStatus || '')
      .trim()
      .toLowerCase();

    if (!allowed.includes(normalizedStatus)) {
      setFormError(validStatusMessage);
      return;
    }

    if (isSameStatusSelected) {
      setFormError('Current project status and new status cannot be the same.');
      return;
    }

    if (!formData.effectiveDate) {
      setFormError('Please select an effective date.');
      return;
    }

    setFormError('');

    const success = await onSubmit({
      ...formData,
      hiddenStatusTime: getCurrentTimeString(),
    });

    if (success === false) return;

    setFormData(DEFAULT_FORM_STATE);
    handleClose();
  };

  if (!isOpen) return null;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      size="medium"
      modalClassName="request-history-submodal"
      headerAction={headerAction}
    >
      <form className="modal-form request-history-edit-form" onSubmit={handleSubmit}>
        {isBlocked && blockedMessage ? (
          <div className="request-history-blocked-message">{blockedMessage}</div>
        ) : null}

        <ProjectNameBanner projectName={projectName} />

        <div className="input-field-container">
          <label className="input-label" htmlFor="add-new-status">
            New status
          </label>
          <div className="field-wrapper is-select">
            <FormControl fullWidth size="small">
              <Select
                id="add-new-status"
                displayEmpty
                value={formData.newStatus}
                onChange={(event) => setFormData((prev) => ({ ...prev, newStatus: event.target.value }))}
                input={<OutlinedInput className="input-field request-history-select" />}
                IconComponent={() => <img src={dropdwn} alt="dropdown" className="input-icon" />}
                disabled={isStatusSelectionDisabled}
                MenuProps={{
                  disablePortal: true,
                  PaperProps: {
                    className: 'request-history-menu',
                  },
                }}
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <span className="dropdown-placeholder">
                        {isLoadingStatusOptions ? 'Loading statuses...' : 'Select status'}
                      </span>
                    );
                  }
                  return selected;
                }}
                required
              >
                {!statusOptions.length ? (
                  <MenuItem disabled value="">
                    {statusOptionsError || 'No statuses available'}
                  </MenuItem>
                ) : statusOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>

        <div className="input-field-container">
          <label className="input-label" htmlFor="add-effective-date">
            Effective date
          </label>
          <div className="field-wrapper is-date-picker request-history-date-picker">
            <CommonDatePicker
              value={formData.effectiveDate ? dayjs(formData.effectiveDate, 'YYYY-MM-DD') : null}
              onChange={(newValue) => {
                if (!newValue || !dayjs(newValue).isValid()) {
                  setFormData((prev) => ({ ...prev, effectiveDate: '' }));
                  return;
                }
                setFormData((prev) => ({ ...prev, effectiveDate: dayjs(newValue).format('YYYY-MM-DD') }));
              }}
              label=""
              placeholder="Select date"
              isDisabled={isBlocked || isSubmitDisabled}
              error={false}
              helperText=""
              displayFormat="YYYY-MM-DD"
            />
          </div>
        </div>

        <div className="input-field-container">
          <label className="input-label" htmlFor="add-reason">
            Remarks
          </label>
          <div className="field-wrapper">
            <textarea
              id="add-reason"
              rows="3"
              className="input-field request-history-textarea"
              value={formData.remarks}
              onChange={(event) => setFormData((prev) => ({ ...prev, remarks: event.target.value }))}
              placeholder="Remarks for status change"
              disabled={isBlocked || isSubmitDisabled}
            />
          </div>
        </div>

        {formError || statusOptionsError ? (
          <div className="form-error">{formError || statusOptionsError}</div>
        ) : null}

        <div className="modal-actions">
          <button type="button" className="btn-cancel" onClick={handleClose}>
            {isBlocked ? 'Close' : cancelLabel}
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSubmit}
            disabled={isBlocked || isSubmitDisabled || isSubmitBlockedByValidation}
          >
            {submitLabel}
          </button>
        </div>
      </form>
    </ModalShell>
  );
};

ProjectNameBanner.propTypes = {
  projectName: PropTypes.string,
};

AddRequestModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  projectName: PropTypes.string,
  currentStatus: PropTypes.string,
  isBlocked: PropTypes.bool,
  isSubmitDisabled: PropTypes.bool,
  blockedMessage: PropTypes.string,
  title: PropTypes.string,
  submitLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  headerAction: PropTypes.node,
};

export default AddRequestModal;
